import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AvatarService } from '@maximizer/core/shared/data-access';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { catchError, of, switchMap } from 'rxjs';
import { AnswerChipListComponent } from './answer-chip-list.component';
import { AnswerChipList, AnswerProfile } from './answer-widget.model';

// The avatar's look lives in the template: the AI app's stylesheet has no shared `.avatar` class.
@Component({
  selector: 'ai-answer-profile',
  templateUrl: './answer-profile.component.html',
  styleUrl: './answer-widget.host.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KENDO_CARD, AnswerChipListComponent],
})
export class AnswerProfileComponent {
  private readonly avatars = inject(AvatarService);

  readonly profile = input.required<AnswerProfile>();

  protected readonly initials = computed(() => initialsOf(this.profile().name));

  /** A badge says what kind of card this is rather than judging it, so it always takes `info`. */
  protected readonly badge = computed<AnswerChipList | null>(() => {
    const label = this.profile().badge;

    return label ? { kind: 'chiplist', items: [{ label, tone: 'info' }] } : null;
  });

  // Follows the key, so a redraw for the same contact asks for no new photo; a failure keeps the
  // initials.
  protected readonly photo = toSignal(
    toObservable(computed(() => this.profile().key)).pipe(
      switchMap((key) => (key ? this.avatars.get(key).pipe(catchError(() => of(''))) : of(''))),
    ),
    { initialValue: '' },
  );
}

// As the CRM writes its avatars: letters only, so a leading bracket or quote is skipped.
function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .map((word) => /\p{L}/u.exec(word)?.[0] ?? '')
    .filter(Boolean);

  if (!letters.length) {
    return '';
  }

  const initials = letters.length > 1 ? `${letters[0]}${letters.at(-1)}` : letters[0];

  return initials.toUpperCase();
}
